import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import './MovieDetails.css'; 
import Loader from './Loader/Loader'; 
import './TopHead.css'; 

const MovieDetails = () => {
    // Get the movie ID from the URL parameters
    const { id } = useParams(); 
    // State to store the movie details
    const [movie, setMovie] = useState(null); 
    // State to control the trailer modal visibility
    const [showModal, setShowModal] = useState(false); 
    // State to store the trailer URL
    const [trailerUrl, setTrailerUrl] = useState(''); 
    // State to store recommended movies
    const [recommendations, setRecommendations] = useState([]); 
    // State to manage loading state
    const [isLoading, setIsLoading] = useState(true); 
    // State to store cast information
    const [casts, setCasts] = useState([]); 

    useEffect(() => {
        const fetchMovieDetails = async () => {
            try {
                // Fetch the TMDB API key from environment variables
                const apiKey = process.env.REACT_APP_TMDB_API_KEY;

                // Fetch movie details, including videos and recommendations
                const response = await fetch(`https://api.themoviedb.org/3/movie/${id}?api_key=${apiKey}&append_to_response=videos,recommendations,credits`);

                // Check if the response is OK (status 200-299)
                if (!response.ok) {
                    throw new Error('Failed to fetch movie details');
                }

                // Parse the JSON response
                const data = await response.json();
                setMovie(data); // Update the movie state with the fetched data

                // Check if there are any videos available
                if (data.videos && data.videos.results.length > 0) {
                    // Find the first video of type 'Trailer'
                    const trailer = data.videos.results.find(video => video.type === 'Trailer');
                    if (trailer) {
                        // Construct the trailer URL with autoplay enabled
                        const trailerUrl = `https://www.youtube.com/embed/${trailer.key}?autoplay=1`;
                        setTrailerUrl(trailerUrl); // Update the trailer URL state
                    } else {
                        console.log('No official trailer found');
                    }
                } else {
                    console.log('No videos available');
                }

                // Check if there are any recommendations available
                if (data.recommendations && data.recommendations.results.length > 0) {
                    // Limit the recommendations to 6 items
                    setRecommendations(data.recommendations.results.slice(0, 6));
                } else {
                    console.log('No recommendations found');
                }

                // Update the cast information
                if (data.credits && data.credits.cast) {
                    // Limit to 6 cast members
                    setCasts(data.credits.cast.slice(0, 6)); 
                } else {
                    console.log('No cast information available');
                }
            } catch (error) {
                // Log any errors that occur during the fetch
                console.error('Error fetching movie details:', error);
            } finally {
                // Set loading state to false when fetch is complete
                setIsLoading(false);
            }
        };

        // Call the fetchMovieDetails function
        fetchMovieDetails();
        // The effect depends on the movie ID
    }, [id]);

    // Function to open the trailer modal
    const openModal = () => setShowModal(true);
    // Function to close the trailer modal
    const closeModal = () => setShowModal(false);

    // Function to handle recommendation click and smooth scroll to top
    const handleRecommendationClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Calculate backdrop image style from random selection
    const backdropStyle = {
        backgroundImage: `url(https://image.tmdb.org/t/p/original${movie?.backdrop_path})`,
    };

    // Display loader while fetching data
    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <div className="movie-details-container" style={backdropStyle}>
                <div className="container">
                    <div className="row">
                        {/* Movie Poster */}
                        <div className="col-md-3 col-12 mb-4">
                            <img src={`https://image.tmdb.org/t/p/w300${movie?.poster_path}`} alt={movie?.title} className="poster-image" />
                        </div>
                        {/* Movie Details */}
                        <div className="col-md-7 col-12 movie-details">
                            <h2>{movie?.title}</h2>
                            <div className='p'>
                                <strong>Date:</strong> {new Date(movie?.release_date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </div>
                            <div className='p'><strong>Rating:</strong> {movie?.adult ? 'R' : 'PG-13'}</div>
                            <div className='p'><strong>Genre:</strong> {movie?.genres?.map(genre => genre.name).join(', ')}</div>
                            <div className='p'><strong>Length:</strong> {`${Math.floor(movie?.runtime / 60)}h ${movie?.runtime % 60}min`}</div>
                            <div className='p'><strong>User Score:</strong> {`${movie?.vote_average * 10}%`}</div>
                            <div className='p'><strong>Description:</strong> {movie?.overview}</div>
                            {/* Watch Trailer Button */}
                            <button onClick={openModal} className="btn btn-primary">Watch Trailer</button>
                       
                       
                        </div>
                         {/* Cast Information */}
                 <div className="container">
                            <h3 className='movie-details movie-head'>Casts</h3>
                            <div className='row'>
                                {casts.length > 0 ? (
                                    // If there are casts, display them
                                    casts.map((cast) => (
                                        <div className="col-md-2 col-4" key={cast.id}>
                                            {/* Display the cast image */}
                                            <img
                                                src={`https://image.tmdb.org/t/p/w185${cast.profile_path}`}
                                                alt={cast.name}
                                                className="thumbnailImage castImage"
                                            />
                                            {/* Display the cast name */}
                                            <p id="cast" className='movie-details linking'>{cast.name}</p>
                                        </div>
                                    ))
                                ) : (
                                    // If no cast information is available
                                    <div>No cast information available</div>
                                )}
                            </div>
                        </div>
                    
                    </div>
                       
                </div>
                
            </div>

            

            {/* Modal */}
            {showModal && (
                <div className="modal-container">
                    <div className="modal-content">
                        <iframe
                            width="560"
                            height="315"
                            src={trailerUrl}
                            title={`${movie?.title} Trailer`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                        <span onClick={closeModal} className="modal-close-btn">Close</span>
                    </div>
                </div>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
                <div className="recommendations-container">
                    <h3 className='movie-head'>Recommended Movies</h3>
                    <div className="row">
                        {recommendations.map((recommendation, index) => (
                            <div key={index} className="col-md-2 col-6 mb-4">
                                <Link to={`/movies/${recommendation.id}`} className="linking" onClick={handleRecommendationClick}>
                                    <img src={`https://image.tmdb.org/t/p/w200${recommendation.poster_path}`} alt={recommendation.title} className="recommended-poster" />
                                    <div className='linking'>{recommendation.title}</div>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default MovieDetails;
